import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import SHA256 from 'crypto-js/sha256'
import md5 from 'crypto-js/md5';
import { NextApiRequest } from "next";
import { v4 as uuidv4 } from 'uuid';
import { JWT_SECRET } from "@/service/constants/system";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * 生成指定长度的数字ID
 * @param length ID长度
 * @returns 数字字符串
 */
export function generateNumericId(length: number): string {
  let result = '';
  const characters = '0123456789';
  const charactersLength = characters.length;
  // 确保第一位不是0，避免看起来像八进制或者被转换
  result += Math.floor(Math.random() * 9 + 1).toString();
  for (let i = 1; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

/**
 * 对密码进行哈希处理 (SHA256)
 * @param password 原始密码
 * @returns 哈希后的密码
 */
export function hashPassword(password: string): string {
  return SHA256(password).toString();
}

/**
 * 对字符串进行哈希处理 (md5)
 * @param str 原始字符串
 * @returns 哈希后的字符串
 */

export function hashString(str: string): string {
  return md5(str).toString();
}

/**
 * 处理变异模型ID，提取真实的模型ID
 * 识别是否包含特定分隔符 @_，如果包含则截断并返回其前面的真实部分，否则直接返回原字符串
 * @param model_key 原始模型ID（可能包含变异后缀）
 * @returns 真实的模型ID
 */
export function getRealModelKey(model_key: string): string {
  if (!model_key) return '';
  if (model_key.includes('@_')) {
    return model_key.split('@_')[0];
  }
  return model_key;
}

export const getIpAddress = (req: NextApiRequest) => {
  let ip: string = '';
  if (req.headers['x-forwarded-for'] && typeof req.headers['x-forwarded-for'] === 'string') {
    ip = req.headers['x-forwarded-for'].split(',')[0];
  } else if (req.headers['x-forwarded-for'] && req.headers['x-forwarded-for'] instanceof Array) {
    ip = req.headers['x-forwarded-for'][0];
  } else {
    ip = req.socket.remoteAddress || '';
  }
  return ip;
}

/**
 * 生成商户订单号
 * 使用 UUID v4 去除连字符，长度为 32 位
 * @returns 32位字符串
 */
export function generateOrderNo(): string {
  // 生成标准的 36 位 UUID (例如: '1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed') 
  const standardUuid = uuidv4();

  // 转换为 32 位 (例如: '1b9d6bcdbbfd4b2d9b5dab8dfbbd4bed') 
  const shortUuid = standardUuid.replace(/-/g, '');

  return shortUuid;
}


export function sendBroadcastChannel(channelKey: string) {
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(channelKey)
    channel.postMessage({ type: 'INSUFFICIENT_BALANCE' })
    channel.close()
  }
}

/**
 * 生成回调URL的签名
 * @param url 完整的回调URL (不包含签名部分)
 * @returns 带有签名的完整URL
 */
export function signWebhookUrl(url: string): string {
  const secret = JWT_SECRET;
  if (!secret) {
    throw new Error('JWT_SECRET is not defined');
  }

  // 使用 URL + secret 进行 md5 哈希作为签名
  const signature = md5(`${url}${secret}`).toString();
  
  // 判断 URL 是否已经有参数
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}sign=${signature}`;
}

/**
 * 验证回调URL的签名
 * @param req NextApiRequest 对象
 * @returns 签名是否有效
 */
export function verifyWebhookSignature(req: NextApiRequest): boolean {
  const secret = JWT_SECRET;
  if (!secret) {
    console.error('JWT_SECRET is not defined during signature verification');
    return false;
  }

  const { sign } = req.query;
  if (!sign || typeof sign !== 'string') {
    return false;
  }

  // 重构原始 URL (不包含 sign 参数)
  const protocol = req.headers['x-forwarded-proto'] || 'http';
  const host = req.headers.host || '';
  const urlObj = new URL(req.url || '', `${protocol}://${host}`);
  
  // 移除 sign 参数
  urlObj.searchParams.delete('sign');
  
  // 这里的 url 应该和生成签名时的 url 完全一致
  const originalUrl = urlObj.toString();
  const expectedSignature = md5(`${originalUrl}${secret}`).toString();

  return expectedSignature === sign;
}